// Das Formular unter einer Modellzeile: Beschreibung, Hersteller, Verbrauch,
// Aufwandsstufen, Preise. Gespeichert wird in der Instanz.

import { useState, type FormEvent } from "react";
import { ApiError, put } from "../../api/client";
import type { InstanceModel, ModelPrice, ModelProfile } from "../../api/types";
import { errorText } from "../../lib/labels";
import { Note } from "../../ui/badge";
import { Input, Textarea } from "../../ui/field";
import { SelectMenu } from "../../ui/select-menu";
import { toast } from "../../ui/toaster";
import { MODEL_ERRORS, PRICE_FIELDS, PROVIDERS, effortOptions, fromProfile, supportedEfforts, toBody, type ModelEditorState } from "./model-editor-map";

export function ModelEditor({ tenantId, model, profile, price, descriptionMax, onSaved }: {
  tenantId: number;
  model: InstanceModel;
  profile: ModelProfile;
  price: ModelPrice | undefined;
  descriptionMax: number;
  onSaved: () => Promise<unknown>;
}) {
  const [s, setS] = useState<ModelEditorState>(() => fromProfile(model, profile, price));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<ModelEditorState>) => setS((cur) => ({ ...cur, ...patch }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await put(`/tenants/${tenantId}/instance/models`, toBody(model.id, s, model));
      toast("Gespeichert");
      setError("");
      await onSaved();
    } catch (err) {
      setError((err instanceof ApiError ? MODEL_ERRORS[err.code] : undefined) ?? errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="pf-model-editor" noValidate onSubmit={(e) => void submit(e)}>
      <div className="pf-grid">
        <label className="pf-field pf-field--wide"><span className="pf-field__label">Beschreibung im Picker (leer = Vorgabe)</span>
          <Textarea className="pf-input--prose" name="description" maxLength={descriptionMax} value={s.description} onChange={(e) => set({ description: e.target.value })} /></label>
        <label className="pf-field"><span className="pf-field__label">Hersteller</span>
          <SelectMenu name="provider" ariaLabel="Hersteller" value={s.provider} options={PROVIDERS} onChange={(provider) => set({ provider })} /></label>
        <label className="pf-field"><span className="pf-field__label">Verbrauch (0–100, leer = keiner)</span>
          <Input name="usage" type="number" min={0} max={100} step={1} value={s.usage} onChange={(e) => set({ usage: e.target.value })} /></label>
      </div>
      {model.effort ? (
        <>
          <div className="pf-field"><span className="pf-field__label">Angebotene Aufwandsstufen</span>
            <div className="pf-row">
              {supportedEfforts(model).map(([e, l]) => (
                <label key={e} className="pf-check">
                  <input
                    type="checkbox" name="efforts" value={e} checked={s.efforts.includes(e)}
                    onChange={(ev) => set({ efforts: ev.target.checked ? [...s.efforts, e] : s.efforts.filter((x) => x !== e) })}
                  />
                  <span className="pf-check__text"><span>{l}</span></span>
                </label>
              ))}
            </div>
          </div>
          <label className="pf-field"><span className="pf-field__label">Standard-Aufwand</span>
            <SelectMenu name="effort" ariaLabel="Standard-Aufwand" value={s.effort} options={effortOptions(model)} onChange={(effort) => set({ effort })} /></label>
        </>
      ) : <Note>An keinem Endpoint dieses Mandanten kommt eine Aufwandsstufe bei diesem Modell an.</Note>}
      <div className="pf-field"><span className="pf-field__label">Preis in USD je 1 Mio. Tokens (alle leer = kein Preis)</span>
        <div className="pf-grid">
          {PRICE_FIELDS.map(([k, l]) => (
            <label key={k} className="pf-field"><span className="pf-field__label">{l}</span>
              <Input name={`price_${k}`} type="number" min={0} step={0.0001} value={s.price[k]} onChange={(e) => set({ price: { ...s.price, [k]: e.target.value } })} /></label>
          ))}
        </div>
      </div>
      <div className="pf-row">
        <button type="submit" className="pf-btn pf-btn--primary pf-btn--sm" disabled={busy}>Speichern</button>
        <span className="pf-field__error">{error}</span>
      </div>
    </form>
  );
}
